// src/pages/university-admin/AssignSupervisor.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    useMediaQuery,
    Stack,
    Autocomplete,
    InputAdornment,
    LinearProgress,
    Fab,
    Zoom
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Search,
    School,
    Work,
    CheckCircle,
    Cancel,
    Schedule,
    Visibility,
    Assignment,
    Verified,
    Pending,
    Info,
    RateReview,
    PersonAdd,
    Email,
    Phone,
    Flag,
    Person,
    Badge as BadgeIcon,
    Wc,
    Add,
    Close,
    Edit,
    Delete,
    SupervisorAccount
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIAssignStudentList,
    APIAssignStudentByUniversity,
    APIAssignStudentStore,
    APIAssignStudentUpdate,
    APIAssignStudentDelete,
    APIAssignStudentShow,
    APIAssignStudentStats,
    APIUnassignedStudents,
    APIAvailableSupervisors,
    APIStudentProfileImage,
    APISupervisorProfileImage
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper functions
const getInitials = (name) => {
    if (!name) return 'U';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const AssignSupervisor = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [stats, setStats] = useState(null);
    const [unassignedStudents, setUnassignedStudents] = useState([]);
    const [availableSupervisors, setAvailableSupervisors] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [assignmentForm, setAssignmentForm] = useState({ 
        student_id: '', 
        supervisor_id: '', 
        assignment_notes: '' 
    });
    const [editForm, setEditForm] = useState({ 
        student_id: '', 
        supervisor_id: '', 
        assignment_notes: '' 
    });
    const [actionLoading, setActionLoading] = useState(false);
    const [studentSearchInput, setStudentSearchInput] = useState('');
    const [supervisorSearchInput, setSupervisorSearchInput] = useState('');
    const [editStudentSearchInput, setEditStudentSearchInput] = useState('');
    const [editSupervisorSearchInput, setEditSupervisorSearchInput] = useState('');
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");
    const universityId = getUserDetails("universityId");

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchAssignments(),
                fetchStats(),
                fetchUnassignedStudents(),
                fetchAvailableSupervisors()
            ]);
        } catch (err) {
            setError("Failed to load assignment data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchAssignments = async () => {
        const result = await apiService.listById(APIAssignStudentByUniversity, universityId);
        if (result.success && isMounted.current) {
            setAssignments(Array.isArray(result.data) ? result.data : []);
        } else {
            console.error("Failed to fetch assignments:", result.message);
        }
    };

    const fetchStats = async () => {
        const result = await apiService.list(APIAssignStudentStats);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const fetchUnassignedStudents = async () => {
        const result = await apiService.listById(APIUnassignedStudents, universityId);
        if (result.success && isMounted.current) {
            setUnassignedStudents(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchAvailableSupervisors = async () => {
        const result = await apiService.listById(APIAvailableSupervisors, universityId);
        if (result.success && isMounted.current) {
            setAvailableSupervisors(Array.isArray(result.data) ? result.data : []);
        }
    };

    const handleAssignSupervisor = async () => {
        if (!assignmentForm.student_id || !assignmentForm.supervisor_id) {
            showToast("Error", "Please select both student and supervisor", "error");
            return;
        }
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('student_id', assignmentForm.student_id);
            formData.append('supervisor_id', assignmentForm.supervisor_id);
            formData.append('assigned_by', adminId);
            if (assignmentForm.assignment_notes) {
                formData.append('assignment_notes', assignmentForm.assignment_notes);
            }
            
            const result = await apiService.create(APIAssignStudentStore, formData);
            
            if (result && result.success) {
                showToast("Success", "Supervisor assigned successfully", "success");
                fetchAllData();
                handleCloseAssignDialog();
            } else {
                showToast("Error", result.message || "Failed to assign supervisor", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to assign supervisor", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateAssignment = async () => {
        if (!selectedAssignment) return;
        
        if (!editForm.student_id || !editForm.supervisor_id) {
            showToast("Error", "Please select both student and supervisor", "error");
            return;
        }
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedAssignment.id);
            formData.append('student_id', editForm.student_id);
            formData.append('supervisor_id', editForm.supervisor_id);
            if (editForm.assignment_notes) {
                formData.append('assignment_notes', editForm.assignment_notes);
            }
            
            const result = await apiService.update(APIAssignStudentUpdate, formData);
            
            if (result && result.success) {
                showToast("Success", "Assignment updated successfully", "success");
                fetchAllData();
                handleCloseEditDialog();
            } else {
                showToast("Error", result.message || "Failed to update assignment", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update assignment", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteAssignment = async () => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedAssignment.id);
            
            const result = await apiService.delete(APIAssignStudentDelete, formData);
            
            if (result && result.success) {
                showToast("Success", "Assignment deleted successfully", "success");
                fetchAllData();
                handleCloseDeleteDialog();
            } else {
                showToast("Error", result.message || "Failed to delete assignment", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to delete assignment", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenAssignDialog = () => {
        setAssignmentForm({ student_id: '', supervisor_id: '', assignment_notes: '' });
        setStudentSearchInput('');
        setSupervisorSearchInput('');
        setOpenAssignDialog(true);
    };

    const handleCloseAssignDialog = () => {
        setOpenAssignDialog(false);
        setAssignmentForm({ student_id: '', supervisor_id: '', assignment_notes: '' });
    };

    const handleOpenEditDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setEditForm({ 
            student_id: assignment.student?.id || '',
            supervisor_id: assignment.supervisor?.id || '',
            assignment_notes: assignment.assignment_notes || '' 
        });
        setEditStudentSearchInput(assignment.student?.name || '');
        setEditSupervisorSearchInput(assignment.supervisor?.name || '');
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setSelectedAssignment(null);
        setEditForm({ student_id: '', supervisor_id: '', assignment_notes: '' });
        setEditStudentSearchInput('');
        setEditSupervisorSearchInput('');
    };

    const handleOpenViewDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedAssignment(null);
    };

    const handleOpenDeleteDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedAssignment(null);
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'active': return <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} />;
            case 'completed': return <Verified sx={{ color: '#2196f3', fontSize: 16 }} />;
            case 'cancelled': return <Cancel sx={{ color: '#f44336', fontSize: 16 }} />;
            default: return <Pending sx={{ color: '#ff9800', fontSize: 16 }} />;
        }
    };

    const getProfileImageUrl = (user, type) => {
        if (user?.profile_image) {
            const imageApi = type === 'student' ? APIStudentProfileImage : APISupervisorProfileImage;
            return getImageUrl(imageApi, user.profile_image);
        }
        return null;
    };

    const filteredAssignments = assignments.filter(assignment => 
        assignment.student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.supervisor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.assignment_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return <LoadingState message="Loading Assignments..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Assignments" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    {/* Renders the top bar which internally displays "Unit Research Lead Portal" */}
                    <InstitutionAdminBar notificationCount={90} />
                    
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <SupervisorAccount /> Assign Supervisor
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<PersonAdd />} 
                                    onClick={handleOpenAssignDialog}
                                    sx={{ textTransform: 'none' }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Assign New Supervisor
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    color="info" 
                                    startIcon={<RefreshIcon />} 
                                    onClick={fetchAllData} 
                                    sx={{ textTransform: 'none' }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Search Bar */}
                    <Paper sx={{ 
                        mb: 3, 
                        p: { xs: 1.5, sm: 2 }, 
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2
                    }}>
                        <Search sx={{ mx: 1, color: 'text.secondary' }} />
                        <TextField 
                            fullWidth 
                            placeholder="Search by student name, supervisor name, or assignment ID..." 
                            variant="standard" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            InputProps={{ disableUnderline: true }}
                            size={isMobile ? "small" : "medium"}
                        />
                    </Paper>
                </Box>
                
                {/* Assignments Table */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 800, sm: 900, md: 1000 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assignment ID</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Student</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supervisor</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assigned Date</TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAssignments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((assignment) => {
                                    const studentAvatarColor = stringToColor(assignment.student?.name);
                                    const studentInitials = getInitials(assignment.student?.name);
                                    const studentProfileImage = getProfileImageUrl(assignment.student, 'student');
                                    
                                    const supervisorAvatarColor = stringToColor(assignment.supervisor?.name);
                                    const supervisorInitials = getInitials(assignment.supervisor?.name);
                                    const supervisorProfileImage = getProfileImageUrl(assignment.supervisor, 'supervisor');
                                    
                                    return (
                                        <TableRow key={assignment.id} hover>
                                            <TableCell>
                                                <Chip 
                                                    label={assignment.assignment_id} 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar 
                                                        src={studentProfileImage}
                                                        sx={{ 
                                                            width: 32, 
                                                            height: 32,
                                                            bgcolor: !studentProfileImage ? studentAvatarColor : 'transparent',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        {!studentProfileImage && studentInitials}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {assignment.student?.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {assignment.student?.email}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar 
                                                        src={supervisorProfileImage}
                                                        sx={{ 
                                                            width: 32, 
                                                            height: 32,
                                                            bgcolor: !supervisorProfileImage ? supervisorAvatarColor : 'transparent',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        {!supervisorProfileImage && supervisorInitials}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {assignment.supervisor?.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {assignment.supervisor?.academic_title}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    icon={getStatusIcon(assignment.status)} 
                                                    label={assignment.formatted_status} 
                                                    size="small" 
                                                    color={assignment.status_color}
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                {formatCreatedAtDate(assignment.assigned_date)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                                    <Tooltip title="View Details">
                                                        <IconButton size="small" onClick={() => handleOpenViewDialog(assignment)}>
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Assignment">
                                                        <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(assignment)}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredAssignments.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No supervisor assignments found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredAssignments.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { 
                            setRowsPerPage(parseInt(e.target.value, 10)); 
                            setPage(0); 
                        }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' }
                            }
                        }}
                    />
                </Paper>

                {/* Assign Supervisor Dialog (Add New) */}
                <Dialog 
                    open={openAssignDialog} 
                    onClose={handleCloseAssignDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonAdd color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Assign Supervisor to Student
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            {/* Student Selection */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                Select Student *
                            </Typography>
                            <Autocomplete
                                id="student-select"
                                options={unassignedStudents}
                                getOptionLabel={(option) => `${option.name} (${option.registration_id})`}
                                value={unassignedStudents.find(s => s.id === assignmentForm.student_id) || null}
                                onChange={(event, newValue) => {
                                    setAssignmentForm({ ...assignmentForm, student_id: newValue?.id || '' });
                                }}
                                inputValue={studentSearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setStudentSearchInput(newInputValue);
                                }}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={getProfileImageUrl(option, 'student')}
                                                sx={{ width: 32, height: 32 }}
                                            >
                                                {!getProfileImageUrl(option, 'student') && getInitials(option.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.email} | {option.registration_id}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Student"
                                        placeholder="Type to search students..."
                                        required
                                        error={!assignmentForm.student_id}
                                        helperText={!assignmentForm.student_id ? "Please select a student" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <School color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 3 }}
                                    />
                                )}
                            />

                            {/* Supervisor Selection */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                Select Supervisor *
                            </Typography>
                            <Autocomplete
                                id="supervisor-select"
                                options={availableSupervisors}
                                getOptionLabel={(option) => `${option.name} (${option.academic_title})`}
                                value={availableSupervisors.find(s => s.id === assignmentForm.supervisor_id) || null}
                                onChange={(event, newValue) => {
                                    setAssignmentForm({ ...assignmentForm, supervisor_id: newValue?.id || '' });
                                }}
                                inputValue={supervisorSearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setSupervisorSearchInput(newInputValue);
                                }}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={getProfileImageUrl(option, 'supervisor')}
                                                sx={{ width: 32, height: 32 }}
                                            >
                                                {!getProfileImageUrl(option, 'supervisor') && getInitials(option.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.academic_title} | {option.research_expertise?.substring(0, 50)}...
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Supervisor"
                                        placeholder="Type to search supervisors..."
                                        required
                                        error={!assignmentForm.supervisor_id}
                                        helperText={!assignmentForm.supervisor_id ? "Please select a supervisor" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Work color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 3 }}
                                    />
                                )}
                            />

                            {/* Assignment Notes */}
                            <TextField
                                fullWidth
                                label="Assignment Notes (Optional)"
                                multiline
                                rows={4}
                                value={assignmentForm.assignment_notes}
                                onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_notes: e.target.value })}
                                placeholder="Add any notes about this assignment..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <RateReview color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 2 }}>
                        <Button onClick={handleCloseAssignDialog} variant="outlined" size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleAssignSupervisor} 
                            variant="contained" 
                            disabled={actionLoading || !assignmentForm.student_id || !assignmentForm.supervisor_id}
                            size={isMobile ? "small" : "medium"}
                        >
                            {actionLoading ? <CircularProgress size={20} /> : 'Assign Supervisor'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Edit Assignment Dialog - Full Edit */}
                <Dialog 
                    open={openEditDialog} 
                    onClose={handleCloseEditDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Edit color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Edit Assignment
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            {/* Current Assignment Info */}
                            <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                    Current Assignment Information
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Assignment ID:</strong> {selectedAssignment?.assignment_id}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Current Student:</strong> {selectedAssignment?.student?.name}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Current Supervisor:</strong> {selectedAssignment?.supervisor?.name}
                                </Typography>
                            </Paper>

                            {/* Student Selection */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                Select New Student *
                            </Typography>
                            <Autocomplete
                                id="edit-student-select"
                                options={unassignedStudents}
                                getOptionLabel={(option) => `${option.name} (${option.registration_id})`}
                                value={unassignedStudents.find(s => s.id === editForm.student_id) || null}
                                onChange={(event, newValue) => {
                                    setEditForm({ ...editForm, student_id: newValue?.id || '' });
                                }}
                                inputValue={editStudentSearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setEditStudentSearchInput(newInputValue);
                                }}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={getProfileImageUrl(option, 'student')}
                                                sx={{ width: 32, height: 32 }}
                                            >
                                                {!getProfileImageUrl(option, 'student') && getInitials(option.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.email} | {option.registration_id}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Student"
                                        placeholder="Type to search students..."
                                        required
                                        error={!editForm.student_id}
                                        helperText={!editForm.student_id ? "Please select a student" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <School color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 3 }}
                                    />
                                )}
                            />

                            {/* Supervisor Selection */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                Select New Supervisor *
                            </Typography>
                            <Autocomplete
                                id="edit-supervisor-select"
                                options={availableSupervisors}
                                getOptionLabel={(option) => `${option.name} (${option.academic_title})`}
                                value={availableSupervisors.find(s => s.id === editForm.supervisor_id) || null}
                                onChange={(event, newValue) => {
                                    setEditForm({ ...editForm, supervisor_id: newValue?.id || '' });
                                }}
                                inputValue={editSupervisorSearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setEditSupervisorSearchInput(newInputValue);
                                }}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={getProfileImageUrl(option, 'supervisor')}
                                                sx={{ width: 32, height: 32 }}
                                            >
                                                {!getProfileImageUrl(option, 'supervisor') && getInitials(option.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.academic_title} | {option.research_expertise?.substring(0, 50)}...
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Supervisor"
                                        placeholder="Type to search supervisors..."
                                        required
                                        error={!editForm.supervisor_id}
                                        helperText={!editForm.supervisor_id ? "Please select a supervisor" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Work color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ mb: 3 }}
                                    />
                                )}
                            />

                            {/* Assignment Notes */}
                            <TextField
                                fullWidth
                                label="Assignment Notes (Optional)"
                                multiline
                                rows={4}
                                value={editForm.assignment_notes}
                                onChange={(e) => setEditForm({ ...editForm, assignment_notes: e.target.value })}
                                placeholder="Add or update assignment notes..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <RateReview color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 2 }}>
                        <Button onClick={handleCloseEditDialog} variant="outlined" size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUpdateAssignment} 
                            variant="contained" 
                            disabled={actionLoading || !editForm.student_id || !editForm.supervisor_id}
                            size={isMobile ? "small" : "medium"}
                        >
                            {actionLoading ? <CircularProgress size={20} /> : 'Update Assignment'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog 
                    open={openViewDialog} 
                    onClose={handleCloseViewDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh',
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Assignment color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Assignment Details
                                </Typography>
                            </Box>
                            <Chip 
                                icon={selectedAssignment && getStatusIcon(selectedAssignment.status)} 
                                label={selectedAssignment?.formatted_status} 
                                color={selectedAssignment?.status_color}
                                size="small"
                            />
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedAssignment && (
                            <Box sx={{ pt: 2 }}>
                                {/* Assignment ID */}
                                <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Assignment Information
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Assignment ID:</strong> {selectedAssignment.assignment_id}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>Assigned Date:</strong> {formatCreatedAtDate(selectedAssignment.assigned_date)}
                                    </Typography>
                                    {selectedAssignment.completion_date && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Completion Date:</strong> {formatCreatedAtDate(selectedAssignment.completion_date)}
                                        </Typography>
                                    )}
                                    {selectedAssignment.assigned_by && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Assigned By:</strong> {selectedAssignment.assigned_by}
                                        </Typography>
                                    )}
                                </Paper>

                                {/* Student Details */}
                                <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <School color="primary" /> Student Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" color="action" />
                                                <strong>Name:</strong> {selectedAssignment.student?.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email fontSize="small" color="action" />
                                                <strong>Email:</strong> {selectedAssignment.student?.email}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone fontSize="small" color="action" />
                                                <strong>Phone:</strong> {selectedAssignment.student?.contact_phone || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <BadgeIcon fontSize="small" color="action" />
                                                <strong>Registration ID:</strong> {selectedAssignment.student?.registration_id}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Supervisor Details */}
                                <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SupervisorAccount color="secondary" /> Supervisor Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" color="action" />
                                                <strong>Name:</strong> {selectedAssignment.supervisor?.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email fontSize="small" color="action" />
                                                <strong>Email:</strong> {selectedAssignment.supervisor?.email}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Work fontSize="small" color="action" />
                                                <strong>Academic Title:</strong> {selectedAssignment.supervisor?.academic_title}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Assignment Notes */}
                                {selectedAssignment.assignment_notes && (
                                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <RateReview color="info" /> Assignment Notes
                                        </Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedAssignment.assignment_notes}
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseViewDialog} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={openDeleteDialog}
                    onClose={handleCloseDeleteDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Delete color="error" sx={{ fontSize: 28 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Delete Assignment
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Are you sure you want to delete this supervisor assignment?
                        </Typography>
                        {selectedAssignment && (
                            <Box sx={{ 
                                p: 2, 
                                bgcolor: theme.palette.grey[50], 
                                borderRadius: 2,
                                mt: 2
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Assignment ID: {selectedAssignment.assignment_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Student: {selectedAssignment.student?.name}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                    Supervisor: {selectedAssignment.supervisor?.name}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
                        <Button onClick={handleCloseDeleteDialog} variant="outlined" size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteAssignment} variant="contained" color="error" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default AssignSupervisor;